import type {
  BackupJobStato,
  ClienteStato,
  ContrattoStato,
  TipoContratto,
  EventSeverity,
  FirmaStato,
  ISODate,
  ISODateTime,
  LeadFonte,
  LeadStato,
  Ruolo,
  StatoCedolino,
  StatoPreventivo,
  TicketPriorita,
  TicketStato,
  TicketFonte,
  TaskPriorita,
  TaskStato,
  TipoCedolinoVoce,
  TipoRelazioneEntita,
  UUID,
} from './entities';

/* ------------------------------------------------------------------ */
/* Transport envelopes                                                 */
/* ------------------------------------------------------------------ */

export interface PaginationMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/** Standard list envelope returned by every `GET /collection` endpoint. */
export interface Paginated<T> {
  data: T[];
  meta: PaginationMeta;
}

export interface PaginationParams {
  page?: number;
  pageSize?: number;
  /** Column to sort by, e.g. `created_at`. */
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  search?: string;
}

export type NotificaCategoriaId =
  | 'ticket'
  | 'backup'
  | 'sicurezza'
  | 'contratti'
  | 'cedolini'
  | 'documenti'
  | 'attivita'
  | 'sla'
  | 'report'
  | 'appuntamenti'
  | 'altro';

export interface NotificaCategoriaCount {
  id: NotificaCategoriaId;
  totale: number;
  nonLette: number;
}

export interface NotificheRiepilogo {
  totale: number;
  nonLette: number;
  categorie: NotificaCategoriaCount[];
}

/** Normalised error shape produced by `lib/errors.ts` from any failure. */
export interface ApiErrorBody {
  statusCode: number;
  message: string | string[];
  error?: string;
  /** Machine-readable code, e.g. `EMAIL_NOT_VERIFIED`. */
  code?: string;
  /** Field-level validation errors keyed by form field name. */
  details?: Record<string, string[]>;
}

/* ------------------------------------------------------------------ */
/* Auth                                                               */
/* ------------------------------------------------------------------ */

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthTokens {
  accessToken: string;
  /** Present only on the legacy JSON body; current sessions use an HttpOnly cookie. */
  refreshToken?: string;
  /** Access-token lifetime in seconds. */
  expiresIn: number;
}

/**
 * The authenticated principal. `cliente_id` is null for internal staff; the
 * backend derives tenant scope from the JWT, so the frontend only uses it for
 * display and for deciding whether to render tenant pickers.
 */
export interface AuthUser {
  id: UUID;
  nome: string;
  email: string;
  telefono: string;
  ruolo: Ruolo;
  cliente_id: UUID | null;
  cliente_ragione_sociale: string | null;
  /** Tenant kind. Absent on staff sessions. */
  cliente_tipo?: 'partner' | 'cliente_finale' | null;
  attivo: boolean;
  mfa_abilitato?: boolean;
  /** ISO timestamp of the first staff tour completion. Null until then. */
  tour_staff_completato_at: string | null;
  /** ISO timestamp of the first customer tour completion. Null until then. */
  tour_cliente_completato_at: string | null;
}

export interface LoginResponse extends AuthTokens {
  user: AuthUser;
  backupCodes?: string[];
}

export interface MfaChallengeResponse {
  status: 'mfa_required' | 'mfa_enrollment';
  mfaToken: string;
}

export type LoginResult = LoginResponse | MfaChallengeResponse;

export function isMfaChallenge(value: LoginResult | LoginResponse): value is MfaChallengeResponse {
  return 'status' in value && (value.status === 'mfa_required' || value.status === 'mfa_enrollment');
}

export interface MfaSetupResponse {
  otpauthUrl: string;
  qrDataUrl: string;
  secret: string;
}

export interface RefreshRequest {
  refreshToken?: string;
}

/* ------------------------------------------------------------------ */
/* Query filters – one interface per list endpoint                     */
/* ------------------------------------------------------------------ */

/** Shared free-text + tenant scoping. `clienteId` is ignored for tenant users. */
export interface BaseFilters extends PaginationParams {
  search?: string;
  clienteId?: UUID;
}

export interface ClienteFilters extends BaseFilters {
  stato?: ClienteStato;
  tipo?: 'partner' | 'cliente_finale';
}

export interface LeadFilters extends PaginationParams {
  stato?: LeadStato[];
  fonte?: LeadFonte;
  assegnato_a?: UUID;
}

export interface LeadInput {
  ragione_sociale?: string | null;
  referente_nome?: string | null;
  referente_email?: string | null;
  referente_telefono?: string | null;
  piva?: string | null;
  codice_fiscale?: string | null;
  fonte?: LeadFonte;
  assegnato_a?: UUID | null;
  note?: string | null;
  consenso_marketing?: boolean;
}

export interface ConvertiLeadInput {
  ragione_sociale?: string;
  crea_contratto_base?: boolean;
  note_conversione?: string;
}

export interface LeadImportIssue {
  riga: number;
  motivo: string;
}

export interface LeadImportPreview {
  import_id: UUID;
  totali: number;
  valide: number;
  scartate: LeadImportIssue[];
  warning: LeadImportIssue[];
  anteprima: Array<{ riga: number; ragione_sociale: string; email: string | null }>;
}

/** `scartate` counts insert failures only. Preview discards stay in the xlsx report. */
export interface LeadImportConfirm {
  inserite: number;
  scartate: number;
  report_url: string;
}

export interface ContrattoFilters extends BaseFilters {
  stato?: ContrattoStato;
  tipo?: TipoContratto;
  famiglia?: 'quadro' | 'canone' | 'una_tantum';
  prodottoId?: UUID;
  servizioId?: UUID;
  gestito?: boolean;
  /** Only contracts overlapping this window. */
  from?: ISODate;
  to?: ISODate;
}

export interface MacchinaFilters extends BaseFilters {
  tipo?: string;
  contrattoId?: UUID;
  sedeId?: UUID;
  entitaId?: UUID;
  isAppliance?: boolean;
}

export const MACHINE_ALERT_STATI = ['error', 'warning', 'ok', 'stale', 'unknown'] as const;
export type MachineAlertStato = (typeof MACHINE_ALERT_STATI)[number];

export const MACHINE_ALERT_SERVIZI = ['backup', 'antivirus', 'firewall', 'syslog'] as const;
export type MachineAlertServizio = (typeof MACHINE_ALERT_SERVIZI)[number];

export const MACHINE_ALERT_VIEWS = ['heatmap', 'table'] as const;
export type MachineAlertView = (typeof MACHINE_ALERT_VIEWS)[number];

export interface MachineAlertsFilters extends BaseFilters {
  stato?: MachineAlertStato[];
  servizio?: MachineAlertServizio[];
  sedeId?: UUID;
  macchinaId?: UUID;
  isAppliance?: boolean;
  recent?: boolean;
  stale?: boolean;
}

export interface MachineAlertServiceState {
  modulo: MachineAlertServizio;
  esito: MachineAlertStato;
  ultimo_esito: string | null;
  ultimo_aggiornamento: ISODateTime;
  stale: boolean;
  recent: boolean;
}

export interface MachineAlertRow {
  id: UUID;
  nome: string;
  hostname: string | null;
  is_appliance: boolean;
  cliente_id: UUID;
  cliente_nome: string;
  sede_id: UUID | null;
  sede_nome: string | null;
  worst: MachineAlertStato;
  stale: boolean;
  recent: boolean;
  ultimo_aggiornamento: ISODateTime;
  servizi: MachineAlertServiceState[];
}

export interface CompanyAlertServiceCounts {
  error: number;
  warning: number;
  ok: number;
  stale: number;
  total: number;
}

export interface CompanyAlert {
  cliente_id: UUID;
  cliente_nome: string;
  totale: number;
  error: number;
  warning: number;
  stale: number;
  ultimo_aggiornamento: ISODateTime;
  servizi: Record<MachineAlertServizio, CompanyAlertServiceCounts>;
  macchine: Array<{
    id: UUID;
    nome: string;
    hostname: string | null;
    worst: MachineAlertStato;
    ultimo_aggiornamento: ISODateTime;
    servizi: MachineAlertServiceState[];
  }>;
}

export interface MachineAlertsKpis {
  totale: number;
  error: number;
  warning: number;
  stale: number;
  critici_24h: number;
  aziende_impattate: number;
  delta_24h: number | null;
  delta_error: number | null;
  delta_warning: number | null;
  serie: TimeSeriesPoint[];
}

export interface MachineAlertsResponse {
  kpis: MachineAlertsKpis;
  companies: CompanyAlert[];
  machines: Paginated<MachineAlertRow>;
  generated_at: ISODateTime;
}

export interface UtenteFilters extends BaseFilters {
  ruolo?: Ruolo;
  attivo?: boolean;
  emailVerificata?: boolean;
  daAttivare?: boolean;
}

export interface TicketFilters extends BaseFilters {
  stato?: TicketStato[];
  priorita?: TicketPriorita[];
  fonte?: TicketFonte[];
  categoria?: string;
  categoriaId?: UUID;
  /** UUID, or `'none'` for unassigned tickets (backend `assegnatoA=none`). */
  assegnatoA?: UUID | 'none';
  macchinaId?: UUID;
  sedeId?: UUID;
  entitaId?: UUID;
  targetType?: 'partner' | 'cliente_finale';
  partnerId?: UUID;
  clienteFinaleId?: UUID;
  /** `true` → only breached, `false` → only within SLA, omitted → all. */
  slaBreached?: boolean;
  from?: ISODateTime;
  to?: ISODateTime;
}

export interface TaskFilters extends BaseFilters {
  stato?: TaskStato[];
  priorita?: TaskPriorita[];
  assegnatoA?: UUID;
  ticketId?: UUID;
  categoriaId?: UUID;
  leadId?: UUID;
  soloMie?: boolean;
  soloInPausa?: boolean;
  /** `da_fare` slice: logged-in assignee, colleagues, or nobody assigned. */
  ambito?: 'mie' | 'colleghi' | 'libere';
}

export interface CreateTaskInput {
  cliente_id?: UUID | null;
  lead_id?: UUID | null;
  categoria_id: UUID;
  ticket_id?: UUID | null;
  entita_collegata_id?: UUID | null;
  sede_id?: UUID | null;
  titolo: string;
  descrizione?: string | null;
  priorita?: TaskPriorita;
  data_scadenza?: ISODateTime | null;
  assegnatari_ids?: UUID[];
}

export interface UpdateTaskInput {
  cliente_id?: UUID | null;
  categoria_id?: UUID;
  titolo?: string;
  descrizione?: string | null;
  stato?: TaskStato;
  priorita?: TaskPriorita;
  motivo_pausa?: string | null;
  data_scadenza?: ISODateTime | null;
  ticket_id?: UUID | null;
  entita_collegata_id?: UUID | null;
  sede_id?: UUID | null;
  assegnatari_ids?: UUID[];
}

export type BackupJobBucket = 'critical' | 'failed' | 'warning' | 'success' | 'other';

export interface BackupJobFilters extends BaseFilters {
  stato?: BackupJobStato[];
  /**
   * Server-side section. Precedence is critical, then failed, then warning,
   * then success — a failed job with a critical alert stays in `critical`.
   */
  bucket?: BackupJobBucket;
  macchinaId?: UUID;
  contrattoId?: UUID;
  from?: ISODateTime;
  to?: ISODateTime;
}

export type BackupPendingTipo = 'vm_backup' | 'configuration_backup';
export type BackupPendingReason = 'unresolved' | 'ambiguous';

export interface BackupPendingGroupFilters extends Omit<BaseFilters, 'clienteId'> {
  tipo?: BackupPendingTipo;
  reason?: BackupPendingReason;
  /** UUID, or `unassigned` for rows still without a customer. */
  clienteId?: UUID | 'unassigned';
  from?: ISODateTime;
  to?: ISODateTime;
}

export interface BackupPendingGroup {
  cliente_id: UUID | null;
  cliente_nome: string | null;
  hint_macchina_nome: string;
  tipo: BackupPendingTipo;
  reason: BackupPendingReason;
  job_count: number;
  prima_data: ISODateTime | null;
  ultima_data: ISODateTime | null;
}

export interface MappaBackupPendingInput {
  cliente_id: UUID;
  source_cliente_id?: UUID | null;
  hint_macchina_nome: string;
  macchina_id?: UUID;
  create_macchina?: {
    nome: string;
    hostname?: string | null;
    tipo?: string | null;
    sede_id?: UUID | null;
    is_appliance?: boolean;
  };
}

export interface ScartaBackupPendingInput {
  cliente_id?: UUID | null;
  source_cliente_id?: UUID | null;
  hint_macchina_nome: string;
  motivo_scarto: string;
}

export interface ServiceEventFilters extends BaseFilters {
  /** Restricts to the events of one module, e.g. `firewall`. */
  modulo?: string;
  eventType?: string;
  source?: string;
  severity?: EventSeverity[];
  macchinaId?: UUID;
  from?: ISODateTime;
  to?: ISODateTime;
}

export interface AuditLogFilters extends BaseFilters {
  tabella?: string;
  utenteId?: UUID;
  from?: ISODateTime;
  to?: ISODateTime;
}

export interface CedolinoFilters extends BaseFilters {
  stato?: StatoCedolino;
  from?: ISODate;
  to?: ISODate;
  /** Multi-select: restrict list/export to these ids. */
  ids?: UUID[];
}

export interface PreventivoFilters extends BaseFilters {
  stato?: StatoPreventivo;
  tipo?: import('./entities').TipoPreventivo;
  periodo_mese?: string;
  stato_cliente?: import('./entities').StatoPreventivoCliente;
  from?: ISODate;
  to?: ISODate;
}

export interface DocumentoFilters extends BaseFilters {
  fonte?: 'wenkey' | 'cliente';
  categoria?:
    | 'generale'
    | 'tecnica'
    | 'contrattuale'
    | 'procedura'
    | 'sicurezza'
    | 'altro';
  tags?: string[];
  includeVersions?: boolean;
  cestino?: boolean;
}

export const CEDOLINO_EXPORT_FORMATS = ['csv', 'xlsx', 'pdf'] as const;
export type CedolinoExportFormat = (typeof CEDOLINO_EXPORT_FORMATS)[number];

/* ------------------------------------------------------------------ */
/* Write payloads                                                     */
/* ------------------------------------------------------------------ */

export interface CreateClienteInput {
  /** Required on create. Partner for staff; cliente_finale for staff or partner admin. */
  tipo: 'partner' | 'cliente_finale';
  /** Required when `tipo = 'cliente_finale'`. */
  partner_id?: UUID;
  ragione_sociale: string;
  partita_iva?: string | null;
  codice_sdi?: string | null;
  email_principale?: string | null;
  telefono?: string | null;
  indirizzo?: string | null;
  stato?: ClienteStato;
  /** End date of the mandatory contratto base. */
  data_scadenza?: ISODate | null;
  split_payment?: boolean;
  costo_orario_senior?: number;
  costo_orario_junior?: number;
}
export type UpdateClienteInput = Partial<Omit<CreateClienteInput, 'tipo' | 'partner_id'>>;

export interface CreateClienteFonteInput {
  tipo: 'nota' | 'link';
  titolo: string;
  descrizione?: string | null;
  contenuto?: string | null;
  url_esterno?: string | null;
}

export interface UpdateClienteFonteInput {
  titolo?: string;
  descrizione?: string | null;
  contenuto?: string | null;
  url_esterno?: string | null;
}

export interface ContrattoRigaInput {
  prodotto_id?: UUID | null;
  servizio_id?: UUID | null;
  gestito?: boolean;
  data_inizio: ISODate;
  data_fine: ISODate;
  stato?: 'attivo' | 'sospeso' | 'scaduto' | 'cancellato';
  note?: string | null;
  prezzo?: number | null;
  macchina_ids?: UUID[];
}

export interface CreateContrattoInput {
  cliente_id: UUID;
  numero_contratto?: string | null;
  tipo?: TipoContratto;
  data_inizio: ISODate;
  data_fine?: ISODate | null;
  stato?: ContrattoStato;
  note?: string | null;
  righe?: ContrattoRigaInput[];
  macchina_ids?: UUID[];
}
export type UpdateContrattoInput = Partial<Omit<CreateContrattoInput, 'cliente_id' | 'righe'>>;
export type UpdateContrattoRigaInput = Partial<ContrattoRigaInput>;

export interface RinnovaContrattoInput {
  data_fine: ISODate;
}

export type DocumentoContrattoTipo =
  | 'contratto_da_firmare'
  | 'contratto_firmato'
  | 'allegato'
  | 'da_firmare'
  | 'firmato';

export interface InviaFirmaInput {
  provider?: string;
  email_cliente?: string;
}

export interface FirmaRichiestaPublic {
  id: UUID;
  provider: string;
  stato: string;
  url_firma: string | null;
  scadenza: ISODateTime | null;
  creato_il: ISODateTime;
  completato_il: ISODateTime | null;
}

export interface FirmaStatoResponse {
  firma_stato: FirmaStato;
  firma_data: ISODateTime | null;
  firma_provider: string | null;
  provider_configurato: boolean;
  provider_disponibili: string[];
  richiesta: FirmaRichiestaPublic | null;
}

export interface CreateCatalogItemInput {
  codice: string;
  nome: string;
  descrizione?: string | null;
  gestito?: boolean;
  attivo?: boolean;
  modulo?: string | null;
  prezzo?: number | null;
}
export type UpdateCatalogItemInput = Partial<CreateCatalogItemInput>;

export interface CatalogImportIssue {
  row: number;
  message: string;
}

export interface CatalogImportSkip {
  codice: string;
  motivo: string;
}

export interface CatalogImportResult<T = unknown> {
  created: T[];
  skipped: CatalogImportSkip[];
  issues: CatalogImportIssue[];
  created_count: number;
  skipped_count: number;
}

export interface CreateMacchinaInput {
  cliente_id: UUID;
  sede_id?: UUID | null;
  nome: string;
  tipo?: string | null;
  indirizzo_ip?: string | null;
  sistema_operativo?: string | null;
  hostname?: string | null;
  marca?: string | null;
  modello?: string | null;
  numero_seriale?: string | null;
  cpu?: string | null;
  ram_gb?: number | null;
  disco?: string | null;
  note?: string | null;
  is_appliance?: boolean;
  /** Contracts / line items to attach on creation. */
  riga_ids?: UUID[];
  /** @deprecated Prefer riga_ids. */
  contratti_ids?: UUID[];
}
export type UpdateMacchinaInput = Partial<Omit<CreateMacchinaInput, 'cliente_id'>>;

export interface CreateUtenteInput {
  cliente_id: UUID | null;
  nome: string;
  email: string;
  telefono: string;
  ruolo: Ruolo;
  /** When true (default) the backend e-mails an activation link. No password is generated. */
  invia_invito?: boolean;
}
export type UpdateUtenteInput = Partial<Pick<CreateUtenteInput, 'nome' | 'telefono' | 'ruolo'>> & {
  attivo?: boolean;
  seniority?: 'senior' | 'junior';
};

export interface RegisterOperatoreInput {
  nome: string;
  cognome: string;
  email: string;
  password: string;
  telefono: string;
  captchaToken?: string;
}

export interface RegistrationAck {
  message: string;
}

export interface CreateTicketInput {
  target_type?: 'partner' | 'cliente_finale';
  /** Billing partner. Staff must send it; partner users may omit it. */
  partner_id?: UUID;
  cliente_finale_id?: UUID | null;
  /** Required for staff creating on behalf of a tenant; ignored otherwise. */
  cliente_id?: UUID;
  macchina_id?: UUID | null;
  sede_id?: UUID | null;
  contratto_id?: UUID | null;
  oggetto: string;
  descrizione: string;
  priorita?: TicketPriorita;
  fonte?: TicketFonte;
  categoria_id?: UUID;
  assegnato_a?: UUID | null;
  /** Extra users notified about the ticket. The creator is always the primary user. */
  stakeholder_ids?: UUID[];
}

export interface UpdateTicketInput {
  oggetto?: string;
  descrizione?: string | null;
  stato?: TicketStato;
  priorita?: TicketPriorita;
  categoria_id?: UUID | null;
  assegnato_a?: UUID | null;
  macchina_id?: UUID | null;
  sede_id?: UUID | null;
  contratto_id?: UUID | null;
  /** Required (minutes, > 0) when moving the ticket to `risolto` or `chiuso`. */
  tempo_risoluzione?: number;
}

export interface CreateCommentoInput {
  corpo: string;
  /** Staff-only internal note. Tenant roles cannot set this. */
  interno?: boolean;
}

export interface CreateCategoriaInput {
  codice: string;
  nome: string;
  descrizione?: string | null;
}

export type UpdateCategoriaInput = Partial<CreateCategoriaInput> & { attiva?: boolean };

export interface CedolinoVoceInput {
  tipo: TipoCedolinoVoce;
  descrizione?: string | null;
  importo?: number;
  quantita?: number | null;
  ticket_id?: UUID | null;
  utente_id?: UUID | null;
  riferimento_data?: ISODate | null;
}

export interface CreateCedolinoInput {
  cliente_id: UUID;
  /** Optional starting entity for billing climb. */
  entita_collegata_id?: UUID | null;
  note?: string | null;
  data_emissione?: ISODate;
  includi_ticket?: boolean;
  from?: ISODate;
  to?: ISODate;
  voci?: CedolinoVoceInput[];
}

export interface UpdateCedolinoInput {
  note?: string | null;
  stato?: 'bozza' | 'in_approvazione';
  voci_add?: CedolinoVoceInput[];
  voci_remove?: UUID[];
}

export type PreventivoOrigine = 'cedolini' | 'prodotti' | 'servizi';

export interface PreventivoVoceInput {
  id: UUID;
  quantita: number;
  /** Unit price for this quote. Overrides the catalog list price when set. */
  prezzo_unitario?: number;
}

export interface CreatePreventivoInput {
  cliente_id?: UUID;
  lead_id?: UUID;
  /** Defaults to cedolini so older callers keep aggregating payroll slips. */
  origine?: PreventivoOrigine;
  cedolino_ids?: UUID[];
  voci?: PreventivoVoceInput[];
  causale?: string;
  condizioni_pagamento?: 'TP01' | 'TP02';
  modalita_pagamento?: 'MP01' | 'MP02' | 'MP05' | 'MP08' | 'MP12';
  giorni_pagamento?: number;
  iban?: string;
  note?: string | null;
  data_validita?: ISODate;
  aliquota_iva?: number;
}

export interface UpdatePreventivoInput {
  causale?: string;
  condizioni_pagamento?: 'TP01' | 'TP02';
  modalita_pagamento?: 'MP01' | 'MP02' | 'MP05' | 'MP08' | 'MP12';
  giorni_pagamento?: number;
  iban?: string | null;
  note?: string | null;
  data_validita?: ISODate;
}

export interface InviaPreventivoInput {
  invia_email?: boolean;
}

export interface PreventivoConfig {
  denominazione: string;
  indirizzo: string;
  cap: string;
  comune: string;
  provincia: string;
  nazione: string;
  partita_iva: string;
  codice_fiscale: string;
  rea: string;
  capitale_sociale: string;
  socio_unico: boolean;
  telefono: string;
  email: string;
  iban: string;
  beneficiario: string;
  condizioni_pagamento: 'TP01' | 'TP02';
  modalita_pagamento: 'MP01' | 'MP02' | 'MP05' | 'MP08' | 'MP12';
  giorni_pagamento: number;
  giorni_validita: number;
  aliquota_iva: number;
  nota_legale: string;
}

/* ------------------------------------------------------------------ */
/* Dashboard & reporting projections                                  */
/* ------------------------------------------------------------------ */

/** `GET /dashboard/summary` – KPI strip on the customer dashboard. */
export interface DashboardSummary {
  servizi_attivi: number;
  /** 0–100, computed by the backend over `backupSuccessRateWindowDays`. */
  backup_success_rate: number | null;
  backup_success_rate_window_days: number;
  ticket_aperti: number;
  alert_critici: number;
  /** Change vs. the previous equivalent window, in percentage points. */
  trend?: {
    servizi_attivi?: number;
    backup_success_rate?: number;
    ticket_aperti?: number;
    alert_critici?: number;
  };
}

/**
 * `GET /dashboard/modules` – tells the frontend which module widgets to
 * lazy-load. Derived from the tenant's active contracts, so the UI never has
 * to reason about product catalogue rules.
 */
export interface ModuleSummary {
  modulo: string;
  servizio_id: UUID;
  servizio_nome: string;
  /** Alias of servizio_id kept so older widgets keep compiling. */
  prodotto_id: UUID;
  prodotto_nome: string;
  contratti_ids: UUID[];
  macchine_totali: number;
  macchine_ok: number;
  macchine_warning: number;
  macchine_error: number;
  ultimo_aggiornamento: ISODateTime | null;
  /** Module-specific headline metric, e.g. last night's success rate. */
  metrica_principale?: { label: string; value: string | number; tone?: 'success' | 'warning' | 'error' | 'neutral' };
}

/** `GET /dashboard/admin/summary` – cross-tenant metrics for staff. */
export interface AdminDashboardSummary {
  clienti_totali: number;
  clienti_attivi: number;
  clienti_sospesi: number;
  contratti_attivi: number;
  servizi_monitorati: number;
  ticket_aperti: number;
  ticket_in_lavorazione: number;
  sla_breached: number;
  /** Share of machines reporting `ok`, 0–100. */
  service_health: number | null;
  macchine_in_errore: number;
  /** Machines whose latest firewall, antivirus or syslog event is a warning. */
  macchine_in_warning: number;
}

/** A point in any time series returned by the reporting endpoints. */
export interface TimeSeriesPoint {
  /** Bucket start, ISO-8601. */
  bucket: ISODateTime;
  [metric: string]: number | string;
}

export interface CategoryCount {
  key: string;
  label: string;
  value: number;
}

export type ReportGranularity = 'day' | 'week' | 'month';

export interface ReportRangeParams {
  from: ISODateTime;
  to: ISODateTime;
  granularity?: ReportGranularity;
  clienteId?: UUID;
}

/** `GET /reports/backup` */
export interface BackupReport {
  range: { from: ISODateTime; to: ISODateTime };
  totale_job: number;
  job_riusciti: number;
  job_falliti: number;
  job_warning: number;
  success_rate: number | null;
  bytes_trasferiti: number | null;
  serie: TimeSeriesPoint[];
  per_macchina: CategoryCount[];
}

/** `GET /reports/tickets` */
export interface TicketReport {
  range: { from: ISODateTime; to: ISODateTime };
  totale: number;
  aperti: number;
  chiusi: number;
  sla_breached: number;
  /** Average resolution time in hours. */
  tempo_medio_risoluzione_ore: number | null;
  serie: TimeSeriesPoint[];
  per_priorita: CategoryCount[];
  per_categoria: CategoryCount[];
}

/** `GET /reports/events` */
export interface EventsReport {
  range: { from: ISODateTime; to: ISODateTime };
  totale: number;
  serie: TimeSeriesPoint[];
  per_severity: CategoryCount[];
  per_tipo: CategoryCount[];
}

/** `GET /reports/admin/clienti` – one row per tenant for comparison tables. */
export interface ClienteComparisonRow {
  cliente_id: UUID;
  ragione_sociale: string;
  contratti_attivi: number;
  macchine: number;
  ticket_aperti: number;
  sla_breached: number;
  backup_success_rate: number | null;
  service_health: number | null;
}

export interface CreateEntitaInput {
  entita_padre_id?: UUID | null;
  ragione_sociale: string;
  partita_iva?: string | null;
  email?: string | null;
  telefono?: string | null;
  indirizzo?: string | null;
  riceve_servizi?: boolean;
  paga_fattura?: boolean;
  tipo_relazione?: TipoRelazioneEntita;
}
export type UpdateEntitaInput = Partial<CreateEntitaInput>;

export interface CreateSedeInput {
  entita_collegata_id?: UUID | null;
  nome: string;
  indirizzo?: string | null;
  citta?: string | null;
  cap?: string | null;
  provincia?: string | null;
  note?: string | null;
}
export type UpdateSedeInput = Partial<CreateSedeInput>;
